import { ChangeDetectionStrategy, Component, ElementRef, Input, Renderer2, ViewChild, viewChild } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { connectState } from '@examdojo/angular/util';
import { getAnimationAppearDisappear } from '@examdojo/animation';
import { CourseCompositeId, CourseStoreModel } from '@examdojo/category';
import { ButtonComponent } from '@examdojo/core/button';
import { DateTimeFormat, DateTimeService } from '@examdojo/core/date-time';
import { EmptyStateComponent } from '@examdojo/core/empty-state';
import { FormConfirmFn } from '@examdojo/core/form-submit-button';
import {
  DEFAULT_SWIPER_STEPPER_OPTIONS,
  SwiperSlideDirective,
  SwiperStepperComponent,
  SwiperStepperNextDirective,
} from '@examdojo/core/swiper';
import { UnwrapSignalValue } from '@examdojo/core/typescript';
import { ExamDateQuery, ExamDateService, ExamDateStoreModel } from '@examdojo/exam-date';
import { FooterPanelComponent } from '@examdojo/ui/footer-panel';
import { SelectionListComponent } from '@examdojo/ui/selection-list';
import { SwiperProgressComponent } from '@examdojo/ui/swiper';
import { assertNonNullable } from '@examdojo/util/assert';
import { isNotNullish } from '@examdojo/util/nullish';
import { IonContent } from '@ionic/angular/standalone';
import { TranslocoPipe } from '@jsverse/transloco';
import { combineLatest, defer, distinctUntilChanged, filter, map, of, startWith, Subject, switchMap } from 'rxjs';
import { StoryCharacter } from '../../sensei/character.model';
import { DEFAULT_SENSEI_CHARACTER } from '../../sensei/default-sensei-character';
import { SenseiComponent } from '../../sensei/sensei.component';
import { AddCourseFormComponent } from '../add-course-form/add-course-form.component';
import { CourseManagementService } from '../course-management.service';
import { CourseFormDialogService } from './course-form-dialog.service';

@Component({
  selector: 'dojo-course-form-dialog',
  imports: [
    AddCourseFormComponent,
    ButtonComponent,
    FooterPanelComponent,
    IonContent,
    SwiperSlideDirective,
    SwiperStepperComponent,
    SwiperStepperNextDirective,
    TranslocoPipe,
    SwiperProgressComponent,
    SenseiComponent,
    SelectionListComponent,
    EmptyStateComponent,
  ],
  templateUrl: './course-form-dialog.component.html',
  styleUrl: './course-form-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [getAnimationAppearDisappear()],
})
export class CourseFormDialogComponent {
  constructor(
    private readonly courseManagementService: CourseManagementService,
    private readonly courseFormDialogService: CourseFormDialogService,
    private readonly examDateService: ExamDateService,
    private readonly examDateQuery: ExamDateQuery,
    private readonly dateTimeService: DateTimeService,
    private readonly renderer: Renderer2,
  ) {
    this.fetchExamDatesOnSelectedCourseChange().pipe(takeUntilDestroyed()).subscribe();
  }

  @Input() canQuit = true;
  @Input() canSelectExistingSubscriptions = false;
  @ViewChild('container', { static: false }) container!: ElementRef;

  readonly form = new FormGroup({
    courseCompositeId: new FormControl<CourseCompositeId[]>([], Validators.required),
    examDateId: new FormControl<Array<ExamDateStoreModel['id']>>([], Validators.required),
  });

  private readonly swiperStepperComponentRef = viewChild(SwiperStepperComponent);

  private readonly slideIndexToStoryCharacter: Record<number, Partial<StoryCharacter>> = {
    0: {
      speechBubble: 'onboarding.sensei_speech.math_subject',
    },
    1: {
      speechBubble: 'onboarding.sensei_speech.assessment_type',
    },
  };

  readonly senseiCharacter$ = toObservable(this.swiperStepperComponentRef).pipe(
    switchMap((stepper) => stepper?.activeSlideIndex$ ?? of(0)),
    map((slideIndex) => ({ ...DEFAULT_SENSEI_CHARACTER, ...this.slideIndexToStoryCharacter[slideIndex] })),
  );

  private readonly courses$ = this.courseManagementService.courses$;

  private readonly selectedCourseId$ = defer(() =>
    this.form.controls.courseCompositeId.valueChanges.pipe(
      startWith(this.form.controls.courseCompositeId.value),
      map((controlValue) => controlValue?.[0]),
    ),
  );

  readonly selectedCourse$ = this.selectedCourseId$.pipe(
    distinctUntilChanged(),
    switchMap((courseCompositeId) =>
      this.courses$.pipe(map((courses) => courses.find((course) => course.compositeId === courseCompositeId))),
    ),
  );

  readonly swiperStepperOptions = { ...DEFAULT_SWIPER_STEPPER_OPTIONS, autoHeight: true };

  readonly state = connectState({
    selectedCourse: this.selectedCourse$,
    examDateOptions: combineLatest([this.examDateQuery.futureExamDates$, this.selectedCourse$]).pipe(
      map(([entities, selectedCourse]) => {
        if (!selectedCourse) {
          return [];
        }
        return entities.filter(
          (entity) =>
            entity.syllabus_id === selectedCourse.syllabus_id && entity.course_level === selectedCourse.course_level,
        );
      }),
      map((entities) =>
        entities.map(({ id, inferredDateLabel, inferredDate }) => {
          const year = this.dateTimeService.formatDate(inferredDate, DateTimeFormat.LongYear);
          const month = this.dateTimeService.formatDate(inferredDate, DateTimeFormat.TwoDigitMonth);

          return {
            label: inferredDateLabel,
            value: id,
            speechBubble: `onboarding.sensei_speech.exam_month.${month}`,
            translationParams: { year },
          };
        }),
      ),
      startWith([]),
    ),
    senseiCharacter: this.senseiCharacter$,
    speechBubble: this.senseiCharacter$.pipe(
      map(
        ({ speechBubble, translationParams }): UnwrapSignalValue<SenseiComponent['activeSpeechBubble']> => ({
          text: speechBubble ?? '',
          translationParams,
        }),
      ),
    ),
  });

  readonly courseSlideHeightTrigger$$ = new Subject<void>();

  onCourseSelected(course: CourseStoreModel) {
    this.form.controls.courseCompositeId.setValue([course.compositeId]);
    this.form.controls.courseCompositeId.markAsDirty();
  }

  onSyllabusSelected() {
    this.courseSlideHeightTrigger$$.next();
  }

  closeForm() {
    this.form.reset();
    this.courseFormDialogService.dismiss(undefined);
  }

  onScroll(event: CustomEvent) {
    const targetElement = this.container?.nativeElement;
    const scrollTop = event.detail.scrollTop;

    if (!targetElement) {
      return;
    }
    if (scrollTop > 0) {
      this.renderer.addClass(targetElement, 'scrolled');
    } else {
      this.renderer.removeClass(targetElement, 'scrolled');
    }
  }

  readonly formCompleteFn: FormConfirmFn = () => {
    const formValue = this.form.getRawValue();
    const course = this.state.selectedCourse;
    const examDateId = formValue.examDateId?.[0];
    assertNonNullable(course, 'course');
    assertNonNullable(examDateId, 'examDateId');

    this.courseFormDialogService.dismiss({ course, examDateId });
  };

  private fetchExamDatesOnSelectedCourseChange() {
    return this.selectedCourse$.pipe(
      filter(isNotNullish),
      switchMap((selectedCourse) =>
        this.examDateService.fetchAll({
          syllabusAndCourseLevels: {
            syllabusId: selectedCourse.syllabus_id,
            courseLevel: selectedCourse.course_level,
          },
        }),
      ),
    );
  }
}
